/* @flow */

import * as React from 'react';
import classNames from 'classnames';
import colorUtils from 'color';
import styled from '@emotion/styled'

import { hover } from 'app/utils/cssUtils';
import useSheet from '../../jss';
import Checkmark from '../icons/Checkmark';
import WarningMark from '../icons/WarningMark';
import Tappable from '../commons/Tappable';

type Props = {
  enabled?: boolean,
  filled?: boolean,
  hasError?: boolean,
  label: string,
  max?: number,
  min?: number,
  onSelect: () => void,
  sheet: Object,
  selected?: number,
  showFilled?: boolean,
  showSelectionFeedback?: boolean
};

const defaultProps = {
  enabled: false,
  filled: false,
  hasError: false,
  max: undefined,
  min: undefined,
  selected: 0,
  showFilled: false,
  showSelectionFeedback: false
};

function PlayerPositionTab({
  enabled,
  filled,
  hasError,
  label,
  max,
  min,
  onSelect,
  selected,
  sheet: { classes },
  showFilled,
  showSelectionFeedback
}: Props) {
  return (
    <Tappable
      onTap={onSelect}
      className={classNames(classes.playerPositionsNavItem, { active: enabled })}
    >
      <span className={classes.playerPositionsNavItemTitle}>{label}</span>
      {showSelectionFeedback && (
        <SmallText className={classes.playerPositionsNavItemData}>
          {selected}
          {' of '}
          {min === max ? min : `${min}-${max}`}
        </SmallText>
      )}
      {(showSelectionFeedback || showFilled) && (
        <span className={classes.playerPositionStatus}>
          {hasError && <WarningMark direction="topleft" />}
          {!hasError && filled && <Checkmark direction="topleft" />}
        </span>
      )}
    </Tappable>
  );
}

PlayerPositionTab.defaultProps = defaultProps;

function applyStyles(theme: Theme) {
  return {
    playerPositionsNavItem: {
      alignItems: 'center',
      backgroundColor: theme.color.white,
      borderRightColor: colorUtils(theme.color.white)
        .darken(0.15)
        .hex(),
      borderRightStyle: 'solid',
      borderRightWidth: 1,
      color: theme.color.basis1,
      display: 'flex',
      flexGrow: 1,
      flexShrink: 0,
      flexBasis: 60,
      flexDirection: 'column',
      paddingTop: theme.spacing.xsmall,
      paddingBottom: theme.spacing.xsmall,
      position: 'relative',
      textAlign: 'center',
      transition: 'background-color 0.2s, color 0.2s',
      ...hover({
        backgroundColor: theme.color.basis6
      }),
      '&:focus, &:active': {
        backgroundColor: theme.color.basis6
      },
      '&.active': {
        backgroundColor: theme.color.basis6,
        color: theme.color.basis2
      },
      '&:last-child': {
        borderRightWidth: 0
      },
      [theme.mediaQuery.desktopAndUp]: {
        flexShrink: 1
      }
    },
    playerPositionsNavItemTitle: {
      fontWeight: theme.fontWeight.demiBold,
      textTransform: 'uppercase'
    },
    playerPositionsNavItemData: {
      color: theme.color.basis1,
      fontSize: theme.fontSize.tiny,
      fontWeight: theme.fontWeight.normal,
      whiteSpace: 'nowrap'
    },
    playerPositionStatus: {
      bottom: 0,
      display: 'flex',
      left: 0,
      position: 'absolute',
      right: 0,
      top: 0
    }
  };
}

export default useSheet(applyStyles)(PlayerPositionTab);

const SmallText = styled.span`
  line-height: 1;
`;
